export const PLANNER_STORAGE_KEY = 'orbit.planner.v1'
export const PLANNER_VERSION = 3 as const
export const HABIT_XP = 10
export const DEFAULT_PROJECT_ID = 'personal'

export type PlannerAttribute =
  | 'strength' | 'energy' | 'mobility' | 'mind' | 'awareness'
  | 'will' | 'work' | 'capital' | 'creativity' | 'relationships'
export type LegacyCategory = 'body' | 'mind' | 'work' | 'life'
export type AchievementTier = 'bronze' | 'silver' | 'gold'
export type PlannerArea =
  | 'work' | 'business' | 'finance' | 'health' | 'sport' | 'education' | 'growth'
  | 'relationships' | 'family' | 'community' | 'home' | 'creativity' | 'rest' | 'innerState'
export type FinanceType = 'income' | 'expense'
export type FinanceCategory = 'salary' | 'business' | 'food' | 'transport' | 'home' | 'health' | 'education' | 'leisure' | 'other'

export const AREAS: Array<{ id: PlannerArea; label: string; color: string }> = [
  { id: 'work', label: 'Работа', color: '#ff263d' },
  { id: 'business', label: 'Бизнес', color: '#cf1f35' },
  { id: 'finance', label: 'Финансы', color: '#aaa4a2' },
  { id: 'health', label: 'Здоровье', color: '#e85d68' },
  { id: 'sport', label: 'Спорт', color: '#ff4355' },
  { id: 'education', label: 'Образование', color: '#c43b49' },
  { id: 'growth', label: 'Саморазвитие', color: '#d8d4d2' },
  { id: 'relationships', label: 'Отношения', color: '#f2f2ef' },
  { id: 'family', label: 'Семья', color: '#b94753' },
  { id: 'community', label: 'Друзья и сообщество', color: '#8f2632' },
  { id: 'home', label: 'Быт и дом', color: '#6d1b24' },
  { id: 'creativity', label: 'Творчество', color: '#d64a59' },
  { id: 'rest', label: 'Отдых', color: '#918b89' },
  { id: 'innerState', label: 'Внутреннее состояние', color: '#c9c3c1' }
]
export const AREA_LABELS = Object.fromEntries(AREAS.map((item) => [item.id, item.label])) as Record<PlannerArea, string>
export const FINANCE_CATEGORY_LABELS: Record<FinanceCategory, string> = {
  salary: 'Зарплата', business: 'Бизнес', food: 'Еда', transport: 'Транспорт', home: 'Дом',
  health: 'Здоровье', education: 'Образование', leisure: 'Отдых', other: 'Другое'
}

export const ATTRIBUTES: Array<{ id: PlannerAttribute; label: string; icon: string; color: string }> = [
  { id: 'strength', label: 'Сила', icon: '◆', color: '#ff263d' },
  { id: 'energy', label: 'Энергия', icon: '⚡', color: '#ff5a67' },
  { id: 'mobility', label: 'Подвижность', icon: '≈', color: '#d8d4d2' },
  { id: 'mind', label: 'Разум', icon: '◈', color: '#b94753' },
  { id: 'awareness', label: 'Осознанность', icon: '◉', color: '#f2f2ef' },
  { id: 'will', label: 'Воля', icon: '▲', color: '#e11931' },
  { id: 'work', label: 'Дело', icon: '▣', color: '#8f2632' },
  { id: 'capital', label: 'Капитал', icon: '₽', color: '#aaa4a2' },
  { id: 'creativity', label: 'Творчество', icon: '✦', color: '#d64a59' },
  { id: 'relationships', label: 'Связи', icon: '∞', color: '#6d1b24' }
]
export const ATTRIBUTE_LABELS = Object.fromEntries(ATTRIBUTES.map((item) => [item.id, item.label])) as Record<PlannerAttribute, string>
export const ATTRIBUTE_COLORS = Object.fromEntries(ATTRIBUTES.map((item) => [item.id, item.color])) as Record<PlannerAttribute, string>

export type HabitSchedule =
  | { type: 'daily' }
  | { type: 'weekdays'; weekdays: number[] }
  | { type: 'weeklyTarget'; target: number }

export interface PlannerTask {
  id: string
  title: string
  date: string
  primaryAttribute: PlannerAttribute
  secondaryAttribute: PlannerAttribute | null
  projectId: string | null
  points: 5 | 10 | 20
  completed: boolean
  createdAt: string
  area: PlannerArea
}

export interface PlannerHabit {
  id: string
  title: string
  attribute: PlannerAttribute
  color: string
  schedule: HabitSchedule
  createdAt: string
  area: PlannerArea
}

export interface FinanceTransaction {
  id: string
  title: string
  type: FinanceType
  amount: number
  date: string
  category: FinanceCategory
  createdAt: string
}

export interface PlannerProject {
  id: string
  name: string
  color: string
  archived: boolean
  createdAt: string
}

export interface PlannerReward {
  id: string
  title: string
  cost: number
  createdAt: string
}

export interface PlannerRewardClaim {
  id: string
  rewardId: string
  title: string
  cost: number
  claimedAt: string
}

export interface PlannerData {
  version: typeof PLANNER_VERSION
  tasks: PlannerTask[]
  habits: PlannerHabit[]
  habitLog: Record<string, Record<string, boolean>>
  projects: PlannerProject[]
  rewards: PlannerReward[]
  rewardClaims: PlannerRewardClaim[]
  streakShields: number
  shieldLog: Record<string, true>
  recoveryModeUntil: string | null
  financeTransactions: FinanceTransaction[]
}

export interface DayStats { date: string; total: number; completed: number; percent: number; xp: number; won: boolean; protected: boolean }
export interface LevelState { xp: number; level: number; title: string; progress: number; nextLevelXp: number }
export interface StreakState { current: number; best: number }
export interface AttributeProgress { id: PlannerAttribute; label: string; icon: string; color: string; xp: number; level: number; progress: number }
export interface CoinState { earned: number; spent: number; balance: number }
export interface PlannerAchievement { id: string; title: string; description: string; tier: AchievementTier; unlocked: boolean; progress: number }

const ATTRIBUTE_IDS = new Set<PlannerAttribute>(ATTRIBUTES.map((item) => item.id))
const AREA_IDS = new Set<PlannerArea>(AREAS.map((item) => item.id))
const FINANCE_CATEGORY_IDS = new Set<FinanceCategory>(Object.keys(FINANCE_CATEGORY_LABELS) as FinanceCategory[])
const LEGACY_MAP: Record<LegacyCategory, PlannerAttribute> = { body: 'strength', mind: 'mind', work: 'work', life: 'awareness' }
const POINTS = new Set([5, 10, 20])
const SAFE_COLOR = /^#[0-9a-f]{6}$/i

const defaultProject = (): PlannerProject => ({
  id: DEFAULT_PROJECT_ID, name: 'Личное', color: '#ff263d', archived: false, createdAt: new Date().toISOString()
})

export function emptyPlannerData(): PlannerData {
  return {
    version: PLANNER_VERSION, tasks: [], habits: [], habitLog: {}, projects: [defaultProject()],
    rewards: [], rewardClaims: [], streakShields: 1, shieldLog: {}, recoveryModeUntil: null, financeTransactions: []
  }
}

export function dateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}
export function addDays(date: Date, amount: number): Date {
  const copy = new Date(date.getFullYear(), date.getMonth(), date.getDate()); copy.setDate(copy.getDate() + amount); return copy
}
export function parseDateKey(value: string): Date {
  const [year, month, day] = value.split('-').map(Number); return new Date(year || 1970, (month || 1) - 1, day || 1)
}

function cleanText(value: unknown, max = 120): string | null {
  if (typeof value !== 'string') return null
  const text = value.trim().replace(/\s+/g, ' ')
  return text && text.length <= max ? text : null
}
function validDateKey(value: unknown): value is string { return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) }
function cleanAttribute(value: unknown, fallback: PlannerAttribute = 'awareness'): PlannerAttribute {
  if (ATTRIBUTE_IDS.has(value as PlannerAttribute)) return value as PlannerAttribute
  return LEGACY_MAP[value as LegacyCategory] ?? fallback
}
function cleanColor(value: unknown, fallback = '#ff263d'): string { return typeof value === 'string' && SAFE_COLOR.test(value) ? value : fallback }
function inferArea(attribute: PlannerAttribute, projectId?: unknown): PlannerArea {
  if (attribute === 'relationships') return 'relationships'
  if (attribute === 'capital') return 'finance'
  if (attribute === 'work' || projectId === 'orbit-yandex') return 'work'
  if (attribute === 'strength' || attribute === 'energy' || attribute === 'mobility') return 'sport'
  if (attribute === 'mind') return 'education'
  if (attribute === 'creativity') return 'creativity'
  if (attribute === 'awareness' || attribute === 'will') return 'growth'
  return 'home'
}
function cleanArea(value: unknown, attribute: PlannerAttribute, projectId?: unknown): PlannerArea {
  return AREA_IDS.has(value as PlannerArea) ? value as PlannerArea : inferArea(attribute, projectId)
}
function cleanSchedule(value: unknown): HabitSchedule {
  if (!value || typeof value !== 'object') return { type: 'daily' }
  const item = value as Partial<HabitSchedule> & { weekdays?: unknown; target?: unknown }
  if (item.type === 'weekdays' && Array.isArray(item.weekdays)) {
    const weekdays = [...new Set(item.weekdays.map(Number).filter((day) => Number.isInteger(day) && day >= 0 && day <= 6))]
    return weekdays.length ? { type: 'weekdays', weekdays } : { type: 'daily' }
  }
  if (item.type === 'weeklyTarget') return { type: 'weeklyTarget', target: Math.min(7, Math.max(1, Number(item.target) || 1)) }
  return { type: 'daily' }
}

function cleanTask(value: unknown, legacy = false): PlannerTask | null {
  if (!value || typeof value !== 'object') return null
  const item = value as any
  const id = cleanText(item.id, 80); const title = cleanText(item.title)
  if (!id || !title || !validDateKey(item.date) || !POINTS.has(Number(item.points))) return null
  const primaryAttribute = cleanAttribute(legacy ? item.category : item.primaryAttribute)
  const secondary = item.secondaryAttribute == null ? null : cleanAttribute(item.secondaryAttribute)
  return {
    id, title, date: item.date, primaryAttribute,
    secondaryAttribute: secondary === primaryAttribute ? null : secondary,
    projectId: cleanText(item.projectId, 80) ?? DEFAULT_PROJECT_ID,
    points: Number(item.points) as PlannerTask['points'], completed: item.completed === true,
    createdAt: typeof item.createdAt === 'string' ? item.createdAt : new Date().toISOString(),
    area: cleanArea(item.area, primaryAttribute, item.projectId)
  }
}
function cleanHabit(value: unknown, legacy = false): PlannerHabit | null {
  if (!value || typeof value !== 'object') return null
  const item = value as any
  const id = cleanText(item.id, 80); const title = cleanText(item.title)
  if (!id || !title) return null
  const attribute = cleanAttribute(legacy ? item.category : item.attribute)
  return { id, title, attribute, color: cleanColor(item.color), schedule: cleanSchedule(item.schedule), createdAt: typeof item.createdAt === 'string' ? item.createdAt : new Date().toISOString(), area: cleanArea(item.area, attribute) }
}
function cleanProject(value: unknown): PlannerProject | null {
  if (!value || typeof value !== 'object') return null
  const item = value as any; const id = cleanText(item.id, 80); const name = cleanText(item.name, 60)
  if (!id || !name) return null
  return { id, name, color: cleanColor(item.color), archived: item.archived === true, createdAt: typeof item.createdAt === 'string' ? item.createdAt : new Date().toISOString() }
}
function cleanReward(value: unknown): PlannerReward | null {
  if (!value || typeof value !== 'object') return null
  const item = value as any; const id = cleanText(item.id, 80); const title = cleanText(item.title, 80); const cost = Math.floor(Number(item.cost))
  return id && title && cost >= 1 && cost <= 100000 ? { id, title, cost, createdAt: typeof item.createdAt === 'string' ? item.createdAt : new Date().toISOString() } : null
}
function cleanClaim(value: unknown): PlannerRewardClaim | null {
  if (!value || typeof value !== 'object') return null
  const item = value as any; const id = cleanText(item.id, 80); const rewardId = cleanText(item.rewardId, 80); const title = cleanText(item.title, 80); const cost = Math.floor(Number(item.cost))
  return id && rewardId && title && cost >= 1 ? { id, rewardId, title, cost, claimedAt: typeof item.claimedAt === 'string' ? item.claimedAt : new Date().toISOString() } : null
}
function cleanFinanceTransaction(value: unknown): FinanceTransaction | null {
  if (!value || typeof value !== 'object') return null
  const item = value as any; const id = cleanText(item.id, 80); const title = cleanText(item.title, 100)
  const amount = Math.round(Number(item.amount) * 100) / 100
  if (!id || !title || !validDateKey(item.date) || !Number.isFinite(amount) || amount <= 0 || amount > 1_000_000_000) return null
  const type: FinanceType = item.type === 'income' ? 'income' : item.type === 'expense' ? 'expense' : 'expense'
  const category = FINANCE_CATEGORY_IDS.has(item.category as FinanceCategory) ? item.category as FinanceCategory : 'other'
  return { id, title, type, amount, date: item.date, category, createdAt: typeof item.createdAt === 'string' ? item.createdAt : new Date().toISOString() }
}

export function parsePlannerData(raw: string | null): PlannerData {
  if (!raw) return emptyPlannerData()
  try {
    const parsed = JSON.parse(raw) as any
    if (!parsed || (parsed.version !== 1 && parsed.version !== 2 && parsed.version !== 3)) return emptyPlannerData()
    const legacy = parsed.version === 1
    const tasks: PlannerTask[] = Array.isArray(parsed.tasks) ? parsed.tasks.slice(0, 5000).map((item: unknown) => cleanTask(item, legacy)).filter((item: PlannerTask | null): item is PlannerTask => Boolean(item)) : []
    const habits: PlannerHabit[] = Array.isArray(parsed.habits) ? parsed.habits.slice(0, 200).map((item: unknown) => cleanHabit(item, legacy)).filter((item: PlannerHabit | null): item is PlannerHabit => Boolean(item)) : []
    let projects: PlannerProject[] = Array.isArray(parsed.projects) ? parsed.projects.slice(0, 200).map((item: unknown) => cleanProject(item)).filter((item: PlannerProject | null): item is PlannerProject => Boolean(item)) : []
    if (!projects.some((item: PlannerProject) => item.id === DEFAULT_PROJECT_ID)) projects = [defaultProject(), ...projects]
    const projectIds = new Set(projects.map((item: PlannerProject) => item.id)); tasks.forEach((task: PlannerTask) => { if (!task.projectId || !projectIds.has(task.projectId)) task.projectId = DEFAULT_PROJECT_ID })
    const knownHabits = new Set(habits.map((habit: PlannerHabit) => habit.id)); const habitLog: PlannerData['habitLog'] = {}
    if (parsed.habitLog && typeof parsed.habitLog === 'object') for (const [day, values] of Object.entries(parsed.habitLog).slice(0, 5000)) {
      if (!validDateKey(day) || !values || typeof values !== 'object') continue
      const cleanValues: Record<string, boolean> = {}
      for (const [habitId, done] of Object.entries(values as Record<string, unknown>).slice(0, 200)) if (knownHabits.has(habitId) && done === true) cleanValues[habitId] = true
      if (Object.keys(cleanValues).length) habitLog[day] = cleanValues
    }
    const rewards: PlannerReward[] = Array.isArray(parsed.rewards) ? parsed.rewards.slice(0, 200).map((item: unknown) => cleanReward(item)).filter((item: PlannerReward | null): item is PlannerReward => Boolean(item)) : []
    const rewardClaims: PlannerRewardClaim[] = Array.isArray(parsed.rewardClaims) ? parsed.rewardClaims.slice(0, 2000).map((item: unknown) => cleanClaim(item)).filter((item: PlannerRewardClaim | null): item is PlannerRewardClaim => Boolean(item)) : []
    const financeTransactions: FinanceTransaction[] = Array.isArray(parsed.financeTransactions) ? parsed.financeTransactions.slice(0, 10000).map((item: unknown) => cleanFinanceTransaction(item)).filter((item: FinanceTransaction | null): item is FinanceTransaction => Boolean(item)) : []
    const shieldLog: Record<string, true> = {}
    if (parsed.shieldLog && typeof parsed.shieldLog === 'object') for (const [day, active] of Object.entries(parsed.shieldLog as Record<string, unknown>)) if (validDateKey(day) && active === true) shieldLog[day] = true
    return {
      version: PLANNER_VERSION, tasks, habits, habitLog, projects, rewards, rewardClaims,
      streakShields: Math.min(99, Math.max(legacy ? 1 : 0, Math.floor(Number(parsed.streakShields) || 0))),
      shieldLog, recoveryModeUntil: validDateKey(parsed.recoveryModeUntil) ? parsed.recoveryModeUntil : null, financeTransactions
    }
  } catch { return emptyPlannerData() }
}

export function getFinanceSummary(data: PlannerData, month: string) {
  const transactions = data.financeTransactions.filter((item) => item.date.startsWith(`${month}-`))
  const income = transactions.filter((item) => item.type === 'income').reduce((sum, item) => sum + item.amount, 0)
  const expense = transactions.filter((item) => item.type === 'expense').reduce((sum, item) => sum + item.amount, 0)
  return { income, expense, balance: income - expense, transactions }
}

function weekStart(day: string): Date { const date = parseDateKey(day); const offset = (date.getDay() + 6) % 7; return addDays(date, -offset) }
function weeklyCompletions(data: PlannerData, habitId: string, day: string): number {
  const start = weekStart(day)
  return Array.from({ length: 7 }, (_, index) => data.habitLog[dateKey(addDays(start, index))]?.[habitId] === true).filter(Boolean).length
}
export function habitsForDate(data: PlannerData, day: string): PlannerHabit[] {
  const weekday = parseDateKey(day).getDay()
  return data.habits.filter((habit) => {
    if (dateKey(new Date(habit.createdAt)) > day) return false
    if (data.habitLog[day]?.[habit.id]) return true
    if (habit.schedule.type === 'weekdays') return habit.schedule.weekdays.includes(weekday)
    if (habit.schedule.type === 'weeklyTarget') return weeklyCompletions(data, habit.id, day) < habit.schedule.target
    return true
  })
}
export function getDayStats(data: PlannerData, day: string): DayStats {
  const tasks = data.tasks.filter((task) => task.date === day); const habits = habitsForDate(data, day)
  const completedTasks = tasks.filter((task) => task.completed); const completedHabits = habits.filter((habit) => data.habitLog[day]?.[habit.id] === true)
  const total = tasks.length + habits.length; const completed = completedTasks.length + completedHabits.length
  const recovered = data.recoveryModeUntil === day && total > 0 && completed >= Math.ceil(total / 2)
  return { date: day, total, completed, percent: total ? Math.round(completed / total * 100) : 0, xp: completedTasks.reduce((sum, task) => sum + task.points, 0) + completedHabits.length * HABIT_XP, won: total > 0 && (completed === total || recovered), protected: data.shieldLog[day] === true }
}
export function getTotalXp(data: PlannerData): number {
  const taskXp = data.tasks.filter((task) => task.completed).reduce((sum, task) => sum + task.points, 0); const ids = new Set(data.habits.map((habit) => habit.id))
  return taskXp + Object.values(data.habitLog).reduce((sum, log) => sum + Object.entries(log).filter(([id, done]) => done && ids.has(id)).length * HABIT_XP, 0)
}
export function getLevelState(data: PlannerData): LevelState {
  const xp = getTotalXp(data); const level = Math.floor(xp / 100) + 1; const titles = ['Новичок', 'Исследователь', 'Практик', 'Стратег', 'Мастер', 'Архитектор', 'Легенда']
  return { xp, level, title: titles[Math.min(titles.length - 1, Math.floor((level - 1) / 2))] ?? 'Новичок', progress: xp % 100, nextLevelXp: level * 100 }
}
export function getAttributeProgress(data: PlannerData): AttributeProgress[] {
  const xp = Object.fromEntries(ATTRIBUTES.map((item) => [item.id, 0])) as Record<PlannerAttribute, number>
  for (const task of data.tasks) if (task.completed) { xp[task.primaryAttribute] += task.points; if (task.secondaryAttribute) xp[task.secondaryAttribute] += Math.ceil(task.points / 2) }
  const habits = new Map(data.habits.map((habit) => [habit.id, habit])); for (const log of Object.values(data.habitLog)) for (const [id, done] of Object.entries(log)) if (done && habits.has(id)) xp[habits.get(id)!.attribute] += HABIT_XP
  return ATTRIBUTES.map((item) => ({ ...item, xp: xp[item.id], level: Math.floor(xp[item.id] / 100) + 1, progress: xp[item.id] % 100 }))
}
export function getCoinState(data: PlannerData): CoinState {
  const taskCoins = data.tasks.filter((task) => task.completed).reduce((sum, task) => sum + task.points / 5, 0); const ids = new Set(data.habits.map((habit) => habit.id))
  const habitCoins = Object.values(data.habitLog).reduce((sum, log) => sum + Object.entries(log).filter(([id, done]) => done && ids.has(id)).length * 2, 0)
  const earned = taskCoins + habitCoins; const spent = data.rewardClaims.reduce((sum, claim) => sum + claim.cost, 0)
  return { earned, spent, balance: Math.max(0, earned - spent) }
}
export function getStreakState(data: PlannerData, todayKey: string): StreakState {
  const activityDates = new Set([...data.tasks.map((task) => task.date), ...Object.keys(data.habitLog), ...Object.keys(data.shieldLog)])
  const maintained = (day: string) => getDayStats(data, day).won || data.shieldLog[day] === true
  const dates = [...activityDates].filter(maintained).sort(); let best = 0; let run = 0; let previous: Date | null = null
  for (const day of dates) { const current = parseDateKey(day); run = previous && dateKey(addDays(previous, 1)) === day ? run + 1 : 1; best = Math.max(best, run); previous = current }
  let cursor = parseDateKey(todayKey); if (!maintained(todayKey)) cursor = addDays(cursor, -1); let current = 0
  while (current < 3660 && maintained(dateKey(cursor))) { current++; cursor = addDays(cursor, -1) }
  return { current, best }
}
export function getLastDays(data: PlannerData, endDay: string, count = 7): DayStats[] { const end = parseDateKey(endDay); return Array.from({ length: count }, (_, i) => getDayStats(data, dateKey(addDays(end, i - count + 1)))) }
export function getHeatmap(data: PlannerData, endDay: string, count = 35): DayStats[] { return getLastDays(data, endDay, count) }
export function getProjectStats(data: PlannerData): Array<PlannerProject & { total: number; completed: number; percent: number }> {
  return data.projects.filter((project) => !project.archived).map((project) => { const tasks = data.tasks.filter((task) => task.projectId === project.id); const completed = tasks.filter((task) => task.completed).length; return { ...project, total: tasks.length, completed, percent: tasks.length ? Math.round(completed / tasks.length * 100) : 0 } })
}
export function getAchievements(data: PlannerData, todayKey: string): PlannerAchievement[] {
  const actions = data.tasks.filter((task) => task.completed).length + Object.values(data.habitLog).reduce((sum, log) => sum + Object.values(log).filter(Boolean).length, 0)
  const best = getStreakState(data, todayKey).best; const level = getLevelState(data).level
  const definitions = [
    ['actions', 'Действия', actions, [1, 25, 100]], ['streak', 'Серия', best, [3, 14, 30]], ['level', 'Уровень', level, [3, 7, 15]]
  ] as const
  const tiers: AchievementTier[] = ['bronze', 'silver', 'gold']
  return definitions.flatMap(([id, title, value, limits]) => limits.map((limit, index) => ({ id: `${id}-${tiers[index]}`, title: `${title}: ${tiers[index] === 'bronze' ? 'бронза' : tiers[index] === 'silver' ? 'серебро' : 'золото'}`, description: `${value} / ${limit}`, tier: tiers[index]!, unlocked: value >= limit, progress: Math.min(100, Math.round(value / limit * 100)) })))
}
